import {
  apiActivityActivityGetActivityCommodity,
  ApiActivityActivityGetActivityCommodityResponse,
  apiCommodityTravelCommodityDetail,
  ApiCommodityTravelCommodityDetailResponse,
  apiCommodityTravelCommodityMarketingDetail,
  ApiCommodityTravelCommodityMarketingDetailResponse,
  apiCommodityVirtualExchangeGetSkuStock,
  ApiCommodityVirtualExchangeGetSkuStockRequest,
  apiEvaluationEvaluateGetLatestEvaluateList,
  ApiEvaluationEvaluateGetLatestEvaluateListResponse,
} from '@/api'
import { AsyncReturnType, RequiredDeep } from 'vtils/types'
import {
  createModelPlus,
  getSeckillInfo,
  prepareData,
  requiredDeep,
  SeckillInfo,
  submit,
} from '@/utils'
import { defaultIndexTo } from 'vtils'
import { defineProvider } from '@/hooks'
import { Ref } from '@vue/composition-api'

export interface RawBookGoodsModel
  extends RequiredDeep<ApiCommodityTravelCommodityDetailResponse> {
  /** 商品ID */
  goodsId: string

  /** 是否已下架 */
  isOff: boolean

  /** 店铺是否休息中 */
  isRest: boolean

  /** 是否已停业 */
  isClosed: boolean

  /** 是否兑换商品 */
  isExchange: boolean

  /** 是否代金券 */
  isCoupon: boolean

  /** 是否日期库存 */
  isDateStock: boolean

  /** 是否时间库存 */
  isTimeStock: boolean

  /** 是否售罄 */
  isSold: boolean

  /** 评价数量 */
  commentCount: number

  /** 评价星级 */
  commentStar: number

  /** 评价列表 */
  commentList: RequiredDeep<ApiEvaluationEvaluateGetLatestEvaluateListResponse>['list']

  /** 营销数据 */
  marketingData: RequiredDeep<ApiCommodityTravelCommodityMarketingDetailResponse>

  /** 选中的 SKU 索引 */
  skuIndex: number

  /** 秒杀数据 */
  seckillData: RequiredDeep<ApiActivityActivityGetActivityCommodityResponse>['activityData']

  /** 秒杀信息 */
  seckillInfo: SeckillInfo

  /** 是否有秒杀商品状态 */
  hasSeckill: boolean

  /** 是否秒杀商品 */
  isSeckill: boolean

  /** 分享参数 */
  shareParams: any

  /** 虚拟商品sku信息 */
  virtualSkuInfo: {
    /** 总评论 */
    cutStock: number
    /** 好评率(99%) */
    skuId: string
  }

  /** 刷新秒杀 */
  refreshSeckill: () => Promise<void>

  refreshMarketing: () => Promise<void>
}

export type BookGoodsModel = AsyncReturnType<typeof getBookGoodsModel>

export async function getBookGoodsModel(
  goodsId: string,
  parentCommodityId?: string,
  skuId?: string,
  isExchange?: boolean,
) {
  const { goodsData } = await prepareData({
    goodsData: apiCommodityTravelCommodityDetail(
      {
        commodityId: goodsId,
      },
      {
        noErrorToast: true,
      },
    )
      .then(requiredDeep)
      .catch(
        () => ({} as RequiredDeep<ApiCommodityTravelCommodityDetailResponse>),
      ),
  })

  if (!goodsData.commodityId) {
    return createModelPlus<RawBookGoodsModel>({
      isOff: true,
    } as any)
  }

  const skuIndex = defaultIndexTo(
    goodsData.skuList.findIndex(item => !!item.defaultShow),
    0,
  )

  const { commentData, marketingData } = await prepareData({
    commentData: apiEvaluationEvaluateGetLatestEvaluateList({
      commodityServiceId: goodsId,
      pageIndex: 1,
      pageSize: 3,
    }).then(requiredDeep),
    marketingData: apiCommodityTravelCommodityMarketingDetail({
      commodityId: goodsId,
      skuId: goodsData.skuList[skuIndex].skuId,
    }).then(requiredDeep),
  })

  const bookGoodsModel = createModelPlus<RawBookGoodsModel>({
    ...{},
    ...goodsData,
    skuIndex: skuIndex,
    get goodsId() {
      return goodsId
    },
    isExchange: false,
    get isOff() {
      return false
    },
    get isRest() {
      return this.store.storeStatus === 4
    },
    get isClosed() {
      return this.store.storeStatus === 3
    },
    get isCoupon() {
      return +this.commodityClassifyType === 1
    },
    get isDateStock() {
      return +this.stockType === 3 || +this.stockType === 5
    },
    get isTimeStock() {
      return +this.stockType === 5
    },
    get isSold() {
      return false
    },
    get commentCount() {
      return commentData.totalNum
    },
    get commentStar() {
      return commentData.evaluateStat
    },
    get commentList() {
      return commentData.list
    },
    marketingData: marketingData,

    seckillData: {} as any,
    seckillInfo: {} as any,
    get hasSeckill() {
      return !!this.seckillData.activityId
    },
    get isSeckill() {
      return (
        this.hasSeckill && getSeckillInfo(this.seckillData as any).isRunning
      )
    },
    get shareParams() {
      return {
        title: this.commodityTitle,
        desc: this.commoditySubTitle,
        cover: this.coverPic,
      }
    },
    async refreshSeckill() {
      await submit(async $ => {
        $.start()
        const {
          // 获取秒杀数据
          seckillData,
        } = await prepareData({
          seckillData: apiActivityActivityGetActivityCommodity({
            commodityId: goodsId,
          }),
        })
        this.seckillData = ((seckillData.hasActivity &&
          seckillData.activityData) ||
          {}) as any
        this.skuList = this.isSeckill
          ? seckillData.activityData!.commodityInfo!.skuInfo!.map(
              (item, index) =>
                ({
                  skuId: item.skuId!,
                  commodityStock: item.stock!,
                  stockType: 0,
                  skuPic: item.pic!,
                  sellPrice: item.price!,
                  specValue: item.skuDesc!,
                  defaultShow: index === 0,
                } as any),
            )
          : goodsData.skuList
      })
    },
    async refreshMarketing() {
      this.marketingData = await apiCommodityTravelCommodityMarketingDetail({
        commodityId: goodsId,
        skuId: this.skuList[this.skuIndex].skuId,
      }).then(requiredDeep)
    },
  })

  bookGoodsModel.$watch(
    () => bookGoodsModel.skuIndex,
    async () => {
      const { marketingData } = await prepareData({
        marketingData: apiCommodityTravelCommodityMarketingDetail({
          commodityId: goodsId,
          skuId: bookGoodsModel.skuList[bookGoodsModel.skuIndex].skuId,
        }).then(requiredDeep),
      })
      bookGoodsModel.$update({
        marketingData,
      })
    },
  )

  const {
    // 获取秒杀数据
    seckillData,
  } = await prepareData({
    seckillData: apiActivityActivityGetActivityCommodity({
      commodityId: goodsId,
    }),
  })

  bookGoodsModel.seckillData = ((seckillData.hasActivity &&
    seckillData.activityData) ||
    {}) as any
  bookGoodsModel.skuList = bookGoodsModel.isSeckill
    ? seckillData.activityData!.commodityInfo!.skuInfo!.map(
        (item, index) =>
          ({
            skuId: item.skuId!,
            commodityStock: item.stock!,
            stockType: 0,
            skuPic: item.pic!,
            sellPrice: item.price!,
            specValue: item.skuDesc!,
            defaultShow: index === 0,
          } as any),
      )
    : goodsData.skuList

  // 是秒杀商品，倒计时
  if (bookGoodsModel.hasSeckill) {
    if (bookGoodsModel.isSeckill) {
      bookGoodsModel.skuIndex = 0
    }
    const { dispose } = getSeckillInfo(
      bookGoodsModel.seckillData as any,
      info => {
        bookGoodsModel.seckillInfo = info
        if (info.isEnded) {
          dispose?.()
          bookGoodsModel.refreshSeckill()
        }
      },
    )
    bookGoodsModel.$beforeDestroy(() => dispose?.())
  }

  const preOrderRequestData: ApiCommodityVirtualExchangeGetSkuStockRequest = {
    commodityId: goodsId,
    parentCommodityId: parentCommodityId,
    skuId: skuId,
    commodityType: '1',
  }
  if (isExchange) {
    bookGoodsModel.isExchange = true
    const virtualSkuInfo = await apiCommodityVirtualExchangeGetSkuStock(
      preOrderRequestData,
    )

    bookGoodsModel.virtualSkuInfo = {
      cutStock: virtualSkuInfo.cutStock || 0,
      skuId: virtualSkuInfo.skuId || '',
    }
  }

  return bookGoodsModel
}

export const bookGoodsModelProvider = defineProvider<Ref<BookGoodsModel>>()
